/* Copyright (c) 2020-present CloudIO Inc */

import { isEmpty } from 'utils/isEmpty';
import { analyzeSelect } from 'componentregistry/components/Select/SelectAnalyze';
import { isNotEmpty } from '../../../../utils/isEmpty';
import { Platform } from '../../../../platform/Platform';
import {
  AliasType,
  ComponentManager,
  ComponentManagerFn,
  RequiredItem,
  ViewAttributeChooserProp,
} from '../../../../registry/Component.type';
import { ItemType } from '../../../../registry/ComponentRegistry';
import { baseComponentManager } from '../../../../registry/Components';
import {
  baseColumnProps,
  formatFunction,
  isBaseColumnPropertyVisible,
} from '../BaseColumn';
import { defaultValue } from './SelectColumn';
import { Select } from 'componentregistry/components/Select/Select';

const SelectColumnManager: ComponentManagerFn<'SelectColumn'> = (
  platform: Platform,
): ComponentManager<'SelectColumn'> => {
  return {
    ...baseComponentManager<'SelectColumn'>(platform, 'SelectColumn'),
    type: 'SelectColumn',
    name: 'Select Column',
    isPropertyVisible(prop, itemData, { appUid, itemId, pageId }): boolean {
      if (
        itemId &&
        !isBaseColumnPropertyVisible({ appUid, platform, itemId, pageId, prop })
      ) {
        return false;
      }

      switch (prop) {
        case 'optionValues':
          return isEmpty(itemData.comboDatasourceAlias);
        case 'comboDatasourceAlias':
          return isEmpty(
            typeof itemData.optionValues === 'string'
              ? itemData.optionValues
              : itemData.optionValues.value,
          );
        case 'valueAttribute':
        case 'labelAttribute':
        case 'doNotPrefetch':
          return isNotEmpty(itemData.comboDatasourceAlias);
        case 'renderAsPill':
        case 'selectOptionOnBlur':
        case 'selectOptionOnTab':
          return !itemData.multi;
        case 'delimiter':
        case 'hideSelectedOptions':
          return itemData.multi;
        case 'specialCharacters':
          return !!itemData.ignoreSpecialCharacters;
        default:
          return true;
      }
    },
    isChildAllowed: (page, item, childType) =>
      (
        [
          'ColumnFooter',
          'SelectReturnValueMapping',
          'TriggerOnCellClick',
          'TriggerOnChange',
          'TriggerAfterServerValidate',
        ] as ItemType[]
      ).includes(childType),
    defaultValue,
    props: {
      ...baseColumnProps,
      viewAttribute: {
        ...(baseColumnProps.viewAttribute as ViewAttributeChooserProp),
        aliasType: ({ appUid, pageId, itemId }) => {
          const parent = platform.findParentOfType(
            appUid,
            pageId,
            itemId,
            'LOVGrid',
          );
          return parent ? 'LOV' : 'STORE';
        },
        getAttributeDataTypes: (props) => {
          const multi = (props.props as Required<Select>).multi;
          return multi
            ? ['String']
            : ['String', 'Integer', 'Decimal', 'Double', 'UserID', 'YN', 'TF'];
        },
      },
      optionValues: {
        type: 'options',
        label: 'Option Values',
        category: 'Select Options',
        helpText:
          'If the options are fixed, then you can specify here instead of providing a Data Store Alias below. e.g. B:Blue, G:Green, Yellow, Red',
      },
      comboDatasourceAlias: {
        label: 'Data Store Alias',
        type: 'DataSourceAliasChooser',
        category: 'Select Options',
        allowedTypes: ['ReadOnlyDataStore'],
        helpText:
          'Only ReadOnlyDataStore allowed. Use Query Select action to query it externally.',
        required: (item: RequiredItem<'Select'>) =>
          isEmpty(item.props.optionValues),
      },
      valueAttribute: {
        label: 'Value Attribute',
        type: 'ComboAttributeChooser',
        aliasType: 'LOV',
        required: (item: RequiredItem<'Select'>) =>
          !!item.props.comboDatasourceAlias,
        getAttributeDataTypes: () => 'ALL',
        category: 'Select Options',
      },
      labelAttribute: {
        label: 'Label Attribute',
        type: 'ComboAttributeChooser',
        aliasType: 'LOV',
        required: (item: RequiredItem<'Select'>) =>
          !!item.props.comboDatasourceAlias,
        getAttributeDataTypes: () => 'ALL',
        category: 'Select Options',
      },
      doNotPrefetch: {
        label: 'Do Not Prefetch',
        type: 'boolean',
        category: 'Select Options',
      },
      optionsWidth: {
        label: 'Options Width',
        type: 'number',
        category: 'Select Options',
        min: 0,
        max: 1000,
        step: 50,
      },
      multi: {
        label: 'Multi Selection',
        type: 'boolean',
        category: 'Select Options',
      },
      hideSelectedOptions: {
        label: 'Hide Selected Options',
        type: 'boolean',
        category: 'Select Options',
        helpText:
          'Enable the option to hide the selected items from the select dropdown.',
      },
      delimiter: {
        label: 'Delimiter',
        type: 'string',
        category: 'Select Options',
      },
      iconExpression: {
        category: 'Look & Feel',
        label: 'Icon',
        type: 'expressionOnly',
        returnType: () => 'string',
      },
      renderAsPill: {
        category: 'Look & Feel',
        label: 'Render As Pill',
        type: 'boolean',
      },
      serverValidateOnChange: {
        category: 'Validation',
        label: 'Server Validate On Change',
        type: 'boolean',
      },
      selectOptionOnTab: {
        category: 'Select Options',
        label: 'Select Option On Tab',
        type: 'boolean',
        helpText: 'Enable the option to select the topmost option on Tab.',
      },
      selectOptionOnBlur: {
        category: 'Select Options',
        label: 'Select Option On Blur',
        type: 'boolean',
        helpText: 'Enable the option to select the topmost option on Blur.',
      },
      formatFunction,
      ignoreSpecialCharacters: {
        label: 'Ignore Special Characters',
        type: 'boolean',
        helpText: 'Enable this to ignore special characters while searching.',
        category: 'Data',
      },
      specialCharacters: {
        label: 'Special Characters',
        type: 'string',
        helpText:
          'Enter special characters to exclude during the search. Use a semicolon (;) as the delimiter.',
        category: 'Data',
      },
    },
    generateShortId: (item) =>
      typeof item.label === 'string' ? item.label : item.label?.value,
    getDataSourceAliasProperty: (aliasType: AliasType) =>
      aliasType === 'LOV' ? { aliasProp: 'comboDatasourceAlias' } : undefined,
    isDataSourceAliasUsed: (item, alias) =>
      item.props.comboDatasourceAlias === alias,
    getDisplayText: (item) =>
      typeof item.props.label === 'string'
        ? item.props.label
        : item.props.label.value ?? '',
    category: 'Data Grid',
    icon: { iconName: 'io-select', iconType: 'cloudio' },
    description: `**Overview**<br/>
The Select Column is the standard dropdown input for Data Grids. It allows users to pick a value from a predefined list of options, ensuring data consistency by preventing free-text entry errors.
It is versatile and supports both Static Lists (hardcoded options like "High/Medium/Low") and Dynamic Lists (database-driven options like "Active Projects").

**1. Defining the Options Source**<br/>
You must choose one of two ways to populate the dropdown. The properties for one method will hide if the other is in use.
* **Option A: Static List (Option Values):** Best for small, unchanging lists.
    * **Option Values:** Enter a comma-separated string.
    * **Simple:** "Red, Green, Blue" (Value and Label are the same).
    * **Key-Value Pair:** "R:Red, G:Green, B:Blue" (Stores 'R' in the database, displays 'Red' to the user).
* **Option B: Dynamic List (Data Store):** Best for lists managed in the database (e.g., Departments, Categories).
    * **Data Store Alias:** Select the ReadOnly Data Store that contains the options.
    * **Value Attribute:** The field to store (e.g., "department_id").
    * **Label Attribute:** The field to display (e.g., "department_name").

**2. Multi-Selection**<br/>
You can configure this column to accept multiple values in a single cell.
* **Multi Selection:**
    * **Enabled:** Users can check multiple boxes in the dropdown.
    * **Storage:** The system stores the selected IDs as a single string, separated by a delimiter.
    * **Delimiter:** The character used to separate values (default is "~"). (Example: If user selects IDs 101 and 102, the stored value is "101~102").
    * **Hide Selected Options:** If true, options vanish from the list once selected (useful for "Tagging" workflows).

**3. Visual Customization**<br/>
* **Render As Pill:** Displays the selected value inside a colored "Chip" or "Pill" background, similar to a tag.
* **Icon Expression:** A string expression to dynamically render an icon next to the text. (Example: \`$.status === 'Approved' ? 'check' : 'time'\`).
* **Options Width:** Manually sets the width of the dropdown menu if the labels are long and getting cut off.

**4. User Experience (UX)**<br/>
* **Select Option On Blur / Tab:**
    * **Enabled:** If the user types "Man" and hits Tab, the system automatically selects "Manager" (the first match). Speeds up heads-down data entry.
* **Do Not Prefetch:**
    * **Enabled:** Delays loading the options until the user clicks the cell. Useful if the dropdown data source is heavy and not always needed.

**5. Advanced Logic**<br/>
* **Select Return Value Mapping:** Add this child component to copy extra data from the selected option into other columns (e.g., Selecting a "Part Number" also fills the "Unit Price" column).
* **Server Validate On Change:** Triggers a backend validation check immediately after selection.`,
    excludeChildren: true,
    replaceableWith: [
      'CheckboxColumn',
      'LookupSelectColumn',
      'StringColumn',
      'AsyncSelectColumn',
    ],
    analyze: analyzeSelect,
  };
};

export default SelectColumnManager;
